import { useMemo, useState } from 'react';
import { Avatar, AvatarImage, Badge, Button } from '@monksflow/monks-ui';
import { XIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { WorkspaceResponseDto } from '@monksflow/platform-api-sdk';
import { TagInputField } from '@/shared/ui/form/TagInputField';
import type { SchemaWidget } from '@/shared/ui/SchemaForm';
import { WorkspaceItem } from '@/features/admin/ui/WorkspaceChips';
import { ToolConfigForm } from './ToolConfigForm';
import { hasRequiredKnowledgeBase } from './toolConfigValidation';

interface AddWorkspaceConfigProps {
  toolId: string;
  jsonSchema: Record<string, unknown>;
  availableWorkspaces: WorkspaceResponseDto[];
  onSave: (workspaceIds: string[], data: Record<string, unknown>) => void | Promise<void>;
  onCancel: () => void;
  isSaving: boolean;
  uiSchemaOverride?: Record<string, unknown>;
  /** Control for `kbSelect`. Injected by the page (dependency injection). */
  kbSelectWidget?: SchemaWidget;
  /** Control for `kbMultiSelect`. Injected by the page (dependency injection). */
  kbMultiSelectWidget?: SchemaWidget;
}

export function AddWorkspaceConfig({
  toolId,
  jsonSchema,
  availableWorkspaces,
  onSave,
  onCancel,
  isSaving,
  uiSchemaOverride,
  kbSelectWidget,
  kbMultiSelectWidget,
}: AddWorkspaceConfigProps) {
  const { t } = useTranslation('admin');

  const [selectedWorkspaces, setSelectedWorkspaces] = useState<WorkspaceResponseDto[]>([]);
  const [formData, setFormData] = useState<Record<string, unknown>>({});

  const isConfigValid = useMemo(
    () => hasRequiredKnowledgeBase(jsonSchema, formData),
    [jsonSchema, formData],
  );

  const handleSave = async () => {
    await onSave(
      selectedWorkspaces.map((workspace) => workspace.id),
      formData,
    );
  };

  return (
    <div className="flex flex-col gap-6 rounded-lg border-2 border-primary p-6">
      <div className="flex flex-col gap-1">
        <h3 className="text-lg font-medium text-foreground">
          {t('admin.addWorkspaceConfig.addWorkspaceConfiguration')}
        </h3>
        <p className="text-sm text-muted-foreground">
          {t('admin.addWorkspaceConfig.selectOneOrMoreWorkspaces')}
        </p>
      </div>

      <TagInputField<WorkspaceResponseDto>
        value={selectedWorkspaces}
        onChange={setSelectedWorkspaces}
        options={availableWorkspaces}
        getOptionLabel={(workspace) => workspace.name}
        getOptionValue={(workspace) => workspace.id}
        isOptionEqualToValue={(option, value) => option.id === value.id}
        placeholder={t('admin.addWorkspaceConfig.selectWorkspaces')}
        disabled={isSaving}
        renderOption={(option) => <WorkspaceItem workspace={option} />}
        renderChip={(workspace, onDelete) => (
          <Badge className="gap-1 rounded-full">
            <Avatar className="size-4">
              <AvatarImage src={workspace.logoUrl ?? undefined} alt={workspace.name} />
            </Avatar>
            {workspace.name}
            {!isSaving && (
              <button
                type="button"
                aria-label={t('admin.addWorkspaceConfig.removeWorkspace', {
                  name: workspace.name,
                })}
                onClick={onDelete}
              >
                <XIcon aria-hidden="true" className="size-3" />
              </button>
            )}
          </Badge>
        )}
      />

      {selectedWorkspaces.length > 0 && (
        <ToolConfigForm
          toolId={toolId}
          jsonSchema={jsonSchema}
          formData={formData}
          onChange={setFormData}
          disabled={isSaving}
          uiSchemaOverride={uiSchemaOverride}
          kbSelectWidget={kbSelectWidget}
          kbMultiSelectWidget={kbMultiSelectWidget}
        />
      )}

      <div className="flex flex-row justify-end gap-2">
        <Button type="button" variant="outline" onClick={onCancel} disabled={isSaving}>
          {t('admin.addWorkspaceConfig.cancel')}
        </Button>
        <Button
          type="button"
          onClick={() => void handleSave()}
          disabled={isSaving || selectedWorkspaces.length === 0 || !isConfigValid}
        >
          {t('admin.addWorkspaceConfig.saveForWorkspaces', {
            count: selectedWorkspaces.length,
          })}
        </Button>
      </div>
    </div>
  );
}
